//src/components/shared/PaginationFooter.tsx
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Props = {
  page: number;
  perPage: 10 | 20 | 30;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: 10 | 20 | 30) => void;
  label?: string;
};

function buildPages(page: number, totalPages: number) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (page <= 4) return [1, 2, 3, 4, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
  if (page >= totalPages - 3) return [1, 2, 3, "ellipsis", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "ellipsis", page - 1, page, page + 1, "ellipsis", totalPages];
}

export function PaginationFooter({
  page,
  perPage,
  total,
  totalPages,
  onPageChange,
  onPerPageChange,
  label = "resultados",
}: Props) {
  const start = total === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);
  const pages = buildPages(page, totalPages);

  return (
    <div className="rounded-xl border bg-background px-4 py-3 shadow-sm">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <div className="text-sm text-muted-foreground">
            Exibindo <span className="font-medium text-foreground">{start}</span> a{" "}
            <span className="font-medium text-foreground">{end}</span> de{" "}
            <span className="font-medium text-foreground">{total}</span> {label}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Por página</span>
            <Select
              value={String(perPage)}
              onValueChange={(value) => onPerPageChange(Number(value) as 10 | 20 | 30)}
            >
              <SelectTrigger className="h-9 w-[86px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="30">30</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <div className="text-sm text-muted-foreground">
            Página <span className="font-medium text-foreground">{page}</span> de{" "}
            <span className="font-medium text-foreground">{totalPages}</span>
          </div>

          <div className="inline-flex items-center overflow-hidden rounded-xl border bg-background">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-10 rounded-none border-r"
              disabled={page <= 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {pages.map((item, index) =>
              item === "ellipsis" ? (
                <div
                  key={`ellipsis-${index}`}
                  className="flex h-9 w-10 items-center justify-center border-r text-muted-foreground"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </div>
              ) : (
                <button
                  key={item}
                  type="button"
                  onClick={() => onPageChange(item as number)}
                  className={`h-9 min-w-10 border-r px-3 text-sm font-medium transition-colors ${
                    page === item
                      ? "bg-primary text-primary-foreground"
                      : "bg-background text-foreground hover:bg-muted"
                  }`}
                >
                  {item}
                </button>
              ),
            )}

            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-10 rounded-none"
              disabled={page >= totalPages}
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
